import React, { useEffect, useMemo, useRef } from 'react';
import hash from 'object-hash';
import { Message } from 'semantic-ui-react';
import type { ReportType } from '../../../../../../../schemas/report.schemas';
import type { DescriptorType } from '../../../../../../../types/descriptor.types';
import Alert from '../../../../../../../utils/alert';
import { findPlaceholderReportedAsLabels } from '../../utils/reportedAsLabelWarnings';
import './reportedAsLabelWarning.css';

type ReportedAsLabelWarningProps = {
    reports: readonly ReportType[];
    descriptors: readonly DescriptorType[];
};

export default function ReportedAsLabelWarning({ reports, descriptors }: ReportedAsLabelWarningProps) {
    const findings = useMemo(() => findPlaceholderReportedAsLabels(reports, descriptors), [reports, descriptors]);
    const currentFindingsHash = useMemo(() => hash(findings), [findings]);
    const previousFindingsHash = useRef<string>();

    const warningContent = useMemo(() => {
        if (findings.length === 0) {
            return null;
        }

        const fieldCount = findings.reduce((count, report) => count + report.fields.length, 0);
        return (
            <div className="financial-label-warning">
                <strong>Missing reported-as labels</strong>
                <p>Found {fieldCount} {fieldCount === 1 ? 'field' : 'fields'} with missing labels in {findings.length} {findings.length === 1 ? 'report' : 'reports'}.</p>
                <div className="financial-label-warning__details">
                    {findings.map(report => (
                        <div key={report.reportId}>
                            <strong>{report.year}/{report.period}/{report.type}/{report.reportType} (ID: {report.reportId})</strong>
                            <ul>
                                {report.fields.map(field => (
                                    <li key={field.descriptorId}>
                                        {field.label} ({field.descriptorId}): &quot;{field.reportedAsLabel}&quot;
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [findings]);

    useEffect(() => {
        const hasChanges = previousFindingsHash.current !== currentFindingsHash;
        previousFindingsHash.current = currentFindingsHash;
        if (hasChanges && warningContent) {
            Alert.warn(warningContent);
        }
    }, [warningContent, currentFindingsHash]);

    return warningContent ? (
        <Message warning role="region" aria-label="Missing reported-as labels">
            {warningContent}
        </Message>
    ) : null;
}
